import { kioskLocation } from "../App/kioskRoutes";
import { ModalCloseButton, AlertIcon } from "../Components/LucidePrimitives";
import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import ToolCredentialDetails from "./Components/ToolCredentialDetails.jsx";
import { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Backend from "../utils/api.js";
import { withRouter } from "react-router-dom";
import SearchBar from "./Components/SearchBar.jsx";
import ToolCard from "./Components/ToolCard.jsx";
import pinpointQrCode from "../assets/pinpoint_qr.png";
import {
  Flex,
  Text,
  Heading,
  SimpleGrid,
  Button,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Image,
  VStack,
  VisuallyHidden,
  HStack,
  Alert,
  AlertDescription,
  useMediaQuery,
} from "@chakra-ui/react";
import ProfileAppBar from "./Components/ProfileAppBar.jsx";
import { useHistory, useLocation } from "react-router-dom";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import KioskActionBar from "../Components/KioskActionBar.jsx";
import { useKioskToast } from "../Components/KioskToast.jsx";
import KioskLoadingAnimation from "../Components/KioskLoadingAnimation.jsx";
import PageIndicator from "../Components/PageIndicator.jsx";
import PaginationArrow from "../Components/PaginationArrow.jsx";

/*
 * Records the tools used during the current visit.
 * Initial check-ins atomically commit a draft; later additions update an existing visit.
 */
function ToolSelection({ user, lab }) {
  const history = useHistory();
  const location = useLocation();
  const reason = location.state?.reason ?? "";
  // Initial visits remain drafts until this screen confirms them atomically.
  const isUpdatingUsage = location.state?.toolUsageFlow === "update";
  const finishSelection = useCallback((usageSaved = false) => history.push(kioskLocation("/kiosk/check-in/complete", history.location), {
    toolUsageFlow: isUpdatingUsage ? "update" : "check-in",
    usageSaved,
  }), [history, isUpdatingUsage]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [permissionLoadingError, setPermissionLoadingError] = useState(false);
  const [noCanvasBadgesIntegrationError, setNoCanvasBadgesIntegrationError] = useState(false);
  const [badgesRateLimited, setBadgesRateLimited] = useState(false);
  const [badgeConnectionExpired, setBadgeConnectionExpired] = useState(false);
  // True when the user has no linked badge-provider account at all. Survives
  // "Continue to tool selection" so the locked-tool modal can tell them
  // to connect Parchment instead of pointing them at Canvas.
  const [badgesNotConnected, setBadgesNotConnected] = useState(false);

  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [machineLoadingError, setMachineLoadingError] = useState(false);
  const [catalogLoadAttempt, setCatalogLoadAttempt] = useState(0);
  const [machines, setMachines] = useState([]); // stores list of machines available in the active lab
  const [query, setQuery] = useState(""); // stores the current search query
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const completionPayloadRef = useRef(null);
  const automaticAttemptRef = useRef(false);
  const [permissionsResolved, setPermissionsResolved] = useState(false);
  const [completionUncertain, setCompletionUncertain] = useState(false);
  const [machineTypes, setMachineTypes] = useState([]) // stores the machineTypes a user has permission to
  const [requireToolSelection, setRequireToolSelection] = useState(true);
  const [usingBadgrIntegration, setUsingBadgrIntegration] = useState(undefined) // stores whether this lab is using canvas badges
  const [allMachineTypes, setAllMachineTypes] = useState([]) // stores all machine types
  const [isPermissionWarningOpen, setIsPermissionWarningOpen] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const [machineTypeWarn, setMachineTypeWarn] = useState("N/A");
  const { show: showToast, toastElements } = useKioskToast();
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [reviewSelected, setReviewSelected] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const [unavailableInstance, setUnavailableInstance] = useState(null);

  const completeSelection = useCallback(async () => {
    if (submittingRef.current) return;
    const selected = completionPayloadRef.current?.machineIds || machines.filter(machine => machine.isSelected).map(machine => machine.id);
    if (!isUpdatingUsage && !location.state?.draftToken) {
      history.replace(kioskLocation("/kiosk/check-in/purpose", history.location));
      return;
    }
    if (isUpdatingUsage && selected.length === 0) {
      finishSelection();
      return;
    }
    submittingRef.current = true;
    completionPayloadRef.current = { draftToken: location.state?.draftToken, machineIds: selected };
    setIsSubmitting(true);
    try {
      const response = isUpdatingUsage
        ? await Backend.newRequest({ UID: user.UID, machineIds: selected, reason, lab: lab.id })
        : await Backend.completeCheckIn(completionPayloadRef.current);
      if (response.status === 200 || response.status === 201) {
        finishSelection(selected.length > 0);
      } else if (response.error === 'purposes_available' && !isUpdatingUsage) {
        history.replace(kioskLocation('/kiosk/check-in/purpose', history.location));
      } else {
        setCompletionUncertain(completionUncertain || response.status === 0);
        if (!completionUncertain && response.status !== 0) completionPayloadRef.current = null;
        showToast("error", response.status === 0
          ? "Could not confirm whether your check-in completed. Please retry before leaving this screen."
          : isUpdatingUsage ? "Could not save your tool usage. Please try again."
          : (response.message || "Could not complete your check-in. Please try again."));
      }
    } catch {
      setCompletionUncertain(true);
      showToast("error", "Could not confirm whether your check-in completed. Please retry before leaving this screen.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [machines, isUpdatingUsage, location.state?.draftToken, history, finishSelection, user.UID, reason, lab.id, completionUncertain, showToast]);

  const toggleCategory = (categoryID) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryID)) {
        next.delete(categoryID);
      } else {
        next.add(categoryID);
      }
      return next;
    });
  };

  // Preserve kiosk pagination when the longer instructions reduce vertical room.
  const [isShortViewport, isVeryShortViewport, isNarrowViewport, isMediumViewport, hasSideArrowSpace] = useMediaQuery([
    "(max-height: 800px)",
    "(max-height: 650px)",
    "(max-width: 767px)",
    "(max-width: 991px)",
    "(min-width: 1440px)",
  ]);
  const columns = isNarrowViewport ? 1 : isMediumViewport ? 2 : 3;
  const TOOLS_PER_PAGE = columns * (isVeryShortViewport ? 1 : isShortViewport ? 2 : 3);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [TOOLS_PER_PAGE]);

  // Ref for arrow key navigation
  const toolGridContainerRef = useRef(null);
  // Handle arrow key navigation for tool cards
  const handleToolGridKeyDown = (e) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      const container = toolGridContainerRef.current;
      if (!container) return;

      // Find all focusable tool cards (Stat elements with role="button" and tabIndex="0")
      const focusableCards = Array.from(container.querySelectorAll('[role="button"][tabIndex="0"]'));
      if (focusableCards.length === 0) return;

      const currentIndex = focusableCards.findIndex(card => card === document.activeElement);

      if (currentIndex === -1) {
        // No card is focused, focus the first one
        if (focusableCards.length > 0) {
          focusableCards[0].focus();
        }
        return;
      }

      let nextIndex;
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        // Move to previous card
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableCards.length - 1;
      } else {
        // Move to next card
        nextIndex = currentIndex < focusableCards.length - 1 ? currentIndex + 1 : 0;
      }

      e.preventDefault();
      focusableCards[nextIndex].focus();
    }
  };

  useDocumentTitle("Select tools – Pinpoint");
  function getUserMachineTypePermissions({ recheck = false } = {}) {
    setPermissionsResolved(false);
    if (recheck) {
      setIsRechecking(true);
    } else {
      setIsLoadingPermissions(true);
    }

    Backend.getMachineTypesByUser(user.UID)
    .then((res) => {
      setIsRechecking(false);
      setIsLoadingPermissions(false);
      if (res.status === 401) {
        // V1 signalled "no badge connection" with a 401.
        setNoCanvasBadgesIntegrationError(true);
        setBadgesNotConnected(true);
        setPermissionLoadingError(false);
        setBadgesRateLimited(false);
      } else if (res.status === 503) {
        setBadgesRateLimited(true);
        setPermissionLoadingError(false);
        setNoCanvasBadgesIntegrationError(false);
      } else if (res.status !== 200 || !Array.isArray(res.data)) {
        setPermissionLoadingError(true);
        setNoCanvasBadgesIntegrationError(false);
        setBadgesRateLimited(false);
      } else {
        // V2 signals "no badge connection" with 200 + authenticated:false
        // rather than V1's 401. Users whose legacy V1 connection lapsed
        // (expired:true -- ~3.5k in prod) get the setup screen too, worded
        // as a reconnect: the old banner alone didn't get them to act.
        const notConnected = res.authenticated === false;
        setPermissionLoadingError(false);
        setNoCanvasBadgesIntegrationError(notConnected);
        setBadgesNotConnected(notConnected);
        setBadgesRateLimited(false);
        setBadgeConnectionExpired(!!res.expired);
        setMachineTypes(res.data || []);
      }
    })
    .catch(() => {
      setIsRechecking(false);
      setIsLoadingPermissions(false);
      setPermissionLoadingError(true);
      setNoCanvasBadgesIntegrationError(false);
      setBadgesRateLimited(false);
    }).finally(() => setPermissionsResolved(true));
  }

  function checkLabBadgrIntegration() {
    Backend.getLabById({id: lab.id, UID: user.UID}).then((res) => {
      if (res.status !== 200 || typeof res.badgrIntegration !== 'boolean') {
        setMachineLoadingError(true);
        return;
      }
      setRequireToolSelection(res.requireToolSelection !== false);
      setUsingBadgrIntegration(res.badgrIntegration);
      if (res.badgrIntegration) {
        getUserMachineTypePermissions();
      }
    }).catch(() => setMachineLoadingError(true));
  }

  function loadAllMachineTypes() {
    Backend.getMachineTypes(user.UID, 1000).then((res) => {
      if (res.error) return;
      delete res.status;
      setAllMachineTypes(Object.values(res)[0]);
    })
  }

  function machineTypeIdToName(id) {
    return allMachineTypes.find((ele) => ele.id.toString() === id.toString());
  }

  useEffect(() => {
    setUsingBadgrIntegration(undefined);
    checkLabBadgrIntegration();
    loadAllMachineTypes();

    let cancelled = false;
    async function loadToolInstances() {
      setIsLoadingMachines(true);
      setMachineLoadingError(false);
      try {
        const categoryResponse = await Backend.getToolCategoriesByLab(user.UID, lab.id, 1000);
        if (categoryResponse.status !== 200 || !Array.isArray(categoryResponse.items)) throw new Error("Category loading failed");
        const activeCategories = categoryResponse.items.filter(cat => !cat.isDeleted);
        const activeCategoryIds = new Set(activeCategories.map(cat => cat.categoryID));
        const availableMachines = await Backend.getMachineByLab(user.UID, lab.id);
        if (availableMachines.status !== 200 || !Array.isArray(availableMachines.items)) throw new Error("Instance loading failed");
        const newMachines = availableMachines.items
          .filter(machine => activeCategoryIds.has(machine.categoryID))
          .map(machine => ({
            ...machine,
            isSelected: false,
            isVisible: true,
            categoryName: activeCategories.find(cat => cat.categoryID === machine.categoryID)?.categoryName || "Unknown",
          }));
        if (!cancelled) {
          setCategories(activeCategories);
          setMachines(newMachines);
        }
      } catch {
        if (!cancelled) setMachineLoadingError(true);
      } finally {
        if (!cancelled) setIsLoadingMachines(false);
      }
    }
    loadToolInstances();
    return () => { cancelled = true; };
  }, [user.UID, lab.id, lab.name, user.firstName, user.lastName, lab.badgrIntegration, catalogLoadAttempt]);

  // filter displayed machines based on search query and selected categories
  useEffect(() => {
    let newMachines = [...machines];

    machines.forEach((machine, index) => {
      const matchesQuery = query.length === 0 ||
        machine.name.toLowerCase().includes(query.toLowerCase()) ||
        machine.categoryName?.toLowerCase().includes(query.toLowerCase());

      const matchesCategory = selectedCategories.size === 0 ||
        selectedCategories.has(machine.categoryID);

      newMachines[index].isVisible = matchesQuery && matchesCategory;
    });

    setMachines(newMachines);
    setCurrentPage(0);
  }, [query, selectedCategories]);

  // Build a flat list of visible machines sorted by category then name
  const isSelectable = machine => (machine.status || '').trim().toLowerCase() !== 'out of service' &&
    (!usingBadgrIntegration || badgesRateLimited || machineTypes.includes(machine.machineType));
  const catalogReady = !isLoadingMachines && !machineLoadingError && usingBadgrIntegration !== undefined && !isLoadingPermissions && !permissionLoadingError && (!usingBadgrIntegration || permissionsResolved);
  const availableCount = machines.filter(isSelectable).length;
  useEffect(() => {
    if (location.state?.noPurposes && !isUpdatingUsage && catalogReady && availableCount === 0 && !automaticAttemptRef.current) {
      automaticAttemptRef.current = true;
      completeSelection();
    }
  }, [location.state?.noPurposes, isUpdatingUsage, catalogReady, availableCount, completeSelection]);
  const selectedCount = machines.filter(m => m.isSelected).length;
  const canCompleteWithoutTools = catalogReady && (availableCount === 0 || (!isUpdatingUsage && !requireToolSelection));
  const completionLabel = isUpdatingUsage
    ? (catalogReady && availableCount === 0 ? "Keep current tool usage" : "Save tool usage")
    : (selectedCount === 0 && canCompleteWithoutTools ? "Complete check-in without tools" : "Complete check-in");
  const visibleMachines = machines
    .filter(m => reviewSelected ? m.isSelected : m.isVisible)
    .sort((a, b) => {
      const catA = categories.find(c => c.categoryID === a.categoryID);
      const catB = categories.find(c => c.categoryID === b.categoryID);
      const catNameA = catA ? catA.categoryName : "zzz";
      const catNameB = catB ? catB.categoryName : "zzz";
      if (catNameA !== catNameB) return catNameA.localeCompare(catNameB);
      return a.name.localeCompare(b.name);
    });

  const totalPages = Math.ceil(visibleMachines.length / TOOLS_PER_PAGE);
  useEffect(() => {
    setCurrentPage(page => Math.min(page, Math.max(0, totalPages - 1)));
  }, [totalPages]);
  useEffect(() => {
    toolGridContainerRef.current?.scrollTo?.(0, 0);
  }, [currentPage, query, selectedCategories, reviewSelected]);
  const pageTools = visibleMachines.slice(
    currentPage * TOOLS_PER_PAGE,
    (currentPage + 1) * TOOLS_PER_PAGE
  );

  // Group current page tools by category for sectioned display
  const categoryGroups = [];
  let currentGroup = null;
  pageTools.forEach((machine) => {
    if (!currentGroup || currentGroup.categoryID !== machine.categoryID) {
      const cat = categories.find(c => c.categoryID === machine.categoryID);
      currentGroup = {
        categoryID: machine.categoryID,
        categoryName: cat ? cat.categoryName : "Other",
        machines: [],
      };
      categoryGroups.push(currentGroup);
    }
    currentGroup.machines.push(machine);
  });

  if (isLoadingPermissions) {
    return (
      <Flex
        minH="100vh"
        bg="gray.50"
        sx={{ touchAction: "manipulation" }}
        pt={5}
        px={8}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <KioskLoadingAnimation />
        <Heading
          textAlign="center"
          fontSize="2xl"
          pt={6}
          fontWeight={500}
          color="gray.600"
        >
          Loading your tool permissions...
        </Heading>
      </Flex>
    );
  } else if (permissionLoadingError) {
    return (
      <Flex
        h="100vh"
        bg="gray.50"
        sx={{ touchAction: "manipulation" }}
        direction="column"
        justify="center"
        align="center"
        px={8}
      >
        <VStack spacing={8} maxW="540px" textAlign="center">
          <VStack spacing={4}>
            <Heading
              fontSize="4xl"
              fontWeight="bold"
              color="gray.800"
            >
              Couldn't Load Your Permissions
            </Heading>
            <Text fontSize="xl" color="gray.600" lineHeight="tall">
              Something went wrong while checking your badges. This is
              usually temporary — tap below to try again.
            </Text>
          </VStack>

          <Box
            bg="white"
            rounded="xl"
            shadow="md"
            px={8}
            py={6}
            w="full"
          >
            <HStack spacing={6} align="start">
              <Image
                src={pinpointQrCode}
                alt="QR Code to Pinpoint"
                boxSize="144px"
                flexShrink={0}
              />
              <VStack align="start" spacing={2}>
                <Text fontSize="xl" fontWeight={600} color="gray.700">
                  If this keeps happening
                </Text>
                <Text fontSize="xl" color="gray.600" textAlign="left" lineHeight="tall">
                  Scan this code on your phone to reconnect your Parchment Digital Badges account
                </Text>
              </VStack>
            </HStack>
          </Box>

          <VStack spacing={0} w="full">
            <Button
              size="kiosk"
              w="full"
              isLoading={isRechecking}
              loadingText="Checking..."
              onClick={() => getUserMachineTypePermissions({ recheck: true })}
            >
              Re-check Badges Connection
            </Button>
            <Button
              variant="link"
              fontSize="xl"
              color="gray.600"
              fontWeight={500}
              onClick={() => setPermissionLoadingError(false)}
              pt={10}
              pb={2}
            >
              Continue to tool selection
            </Button>
          </VStack>
        </VStack>
      </Flex>
    );
  } else if (noCanvasBadgesIntegrationError && !(location.state?.noPurposes && catalogReady && availableCount === 0)) {
    return (
      <Flex
        h="100vh"
        bg="gray.50"
        sx={{ touchAction: "manipulation" }}
        direction="column"
        justify="center"
        align="center"
        px={8}
      >
        <VStack spacing={8} maxW="540px" textAlign="center">
          <VStack spacing={4}>
            <Heading
              fontSize="4xl"
              fontWeight="bold"
              color="gray.800"
            >
              {badgeConnectionExpired ? "Reconnect Your Badges" : "Badges Setup Required"}
            </Heading>
            <Text fontSize="xl" color="gray.600" lineHeight="tall">
              {badgeConnectionExpired
                ? "Pinpoint has moved to a new Parchment Digital Badges connection, and your old one no longer unlocks tools. Reconnect once to restore access."
                : "This space uses Parchment Digital Badges to manage tool access. Connect your account to unlock tools."}
            </Text>
          </VStack>

          <Box
            bg="white"
            rounded="xl"
            shadow="md"
            px={8}
            py={6}
            w="full"
          >
            <HStack spacing={6} align="start">
              <Image
                src={pinpointQrCode}
                alt="QR Code to Pinpoint"
                boxSize="175px"
                flexShrink={0}
              />
              <VStack align="start" spacing={3}>
                <Text fontSize="xl" fontWeight={600} color="gray.700">
                  Set up on your phone
                </Text>
                <VStack align="start" spacing={1} textAlign="left">
                  <Text fontSize="xl" color="gray.600">1. Scan this QR code</Text>
                  <Text fontSize="xl" color="gray.600">2. Go to profile settings</Text>
                  <Text fontSize="xl" color="gray.600">
                    3. Tap "{badgeConnectionExpired ? "Reconnect to Parchment Digital Badges" : "Connect to Parchment Digital Badges"}"
                  </Text>
                </VStack>
              </VStack>
            </HStack>
          </Box>

          <VStack spacing={0} w="full">
            <Button
              size="kiosk"
              w="full"
              isLoading={isRechecking}
              loadingText="Checking..."
              onClick={() => getUserMachineTypePermissions({ recheck: true })}
            >
              Re-check Badges Connection
            </Button>
            <Button
              variant="link"
              fontSize="xl"
              color="gray.600"
              fontWeight={500}
              onClick={() => setNoCanvasBadgesIntegrationError(false)}
              pt={10}
              pb={2}
            >
              Continue to tool selection
            </Button>
          </VStack>
        </VStack>
      </Flex>
    );
  } else {
    return (
      <Flex direction="column" h="100vh" bg="gray.50" sx={{ touchAction: "manipulation" }} overflow="hidden">
        <ProfileAppBar
          cancelPending={isSubmitting || completionUncertain}
          user={user}
          signoutLocation="/kiosk"
          title={`Welcome, ${user.firstName}!`}
          subtitle="Which tools will you use?"
        />
        {badgesRateLimited && (
          <Alert status="warning" px={{ base: 4, md: 8 }} py={3} flexShrink={0}>
            <AlertIcon />
            <AlertDescription fontSize="md">
            We are currently experiencing issues connecting to Parchment Digital Badges. Please only select tools for which you have been qualified.
            </AlertDescription>
          </Alert>
        )}
        {badgeConnectionExpired && (
          <Alert status="warning" px={{ base: 4, md: 8 }} py={3} flexShrink={0} justifyContent="center" textAlign="center">
            <AlertIcon />
            <AlertDescription fontSize="md">
            Your badges connection has expired. Tools that require credentials are unavailable until you reconnect it from your Profile page.
            </AlertDescription>
          </Alert>
        )}
        <Box px={{ base: 4, md: 8 }} pt={3} pb={1} maxW="container.xl" mx="auto" w="full" flexShrink={0}>
          <Flex gap={3} align="center">
            <Box flex={1} minW={0}><SearchBar query={query} setQuery={(value) => { setReviewSelected(false); setQuery(value); }} /></Box>
            {categories.length > 1 && <Button variant="outline" minH="56px" px={4} flexShrink={0}
              onClick={() => setIsCategoryFilterOpen(true)} aria-label="Filter by category" aria-haspopup="dialog">
              Categories{selectedCategories.size > 0 ? ` (${selectedCategories.size})` : ''}
            </Button>}
          </Flex>
        </Box>
        <Flex flex={1} direction="column" px={{ base: 4, md: 8 }} maxW="container.xl" mx="auto" w="full" overflow="visible" minH={0}>

          {machineLoadingError ? (
            <VStack my={12} spacing={5} textAlign="center">
              <Text fontSize="xl" color="gray.600" role="alert">Could not load tools. Please try again.</Text>
              <Button size="kiosk" variant="outline" onClick={() => setCatalogLoadAttempt(value => value + 1)}>Try again</Button>
            </VStack>
          ) : !isLoadingMachines && machines.length === 0 ? (
            <Text my={12} fontSize="xl" color="gray.600" textAlign="center">
              No tools available in this space.
            </Text>
          ) : !isLoadingMachines && visibleMachines.length === 0 ? (
            <Text my={12} fontSize="xl" color="gray.600" textAlign="center">
              {reviewSelected ? "No tools selected. Choose All tools to keep browsing." : "No tools match your search or selected categories."}
            </Text>
          ) : !isLoadingMachines && usingBadgrIntegration !== undefined ? (
              <Flex w="full" align="center" gap={4} flex={1} minH={0} position="relative">
                {hasSideArrowSpace && totalPages > 1 && <Box position="absolute" left="-88px" top="50%" transform="translateY(-50%)">
                  <PaginationArrow direction="left" disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)} />
                </Box>}
                {hasSideArrowSpace && totalPages > 1 && <Box position="absolute" right="-88px" top="50%" transform="translateY(-50%)">
                  <PaginationArrow direction="right" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} />
                </Box>}
                {/* Tool grid with category headers */}
                <Flex
                  ref={toolGridContainerRef}
                  h="full"
                  onKeyDown={handleToolGridKeyDown}
                  flex={1}
                  minW={0}
                  direction="column"
                  justify="flex-start"
                  overflowY="auto"
                  tabIndex={0}
                  aria-label="Tools"
                  role="region"
                  minH={0}
                  py={2}
                  px={2}
                >
                  {categoryGroups.map((group, groupIdx) => (
                    <Box key={`cat-${group.categoryID}`} mb={groupIdx < categoryGroups.length - 1 ? 4 : 0}>
                      <Text
                        fontSize="md"
                        fontWeight={700}
                        color="gray.600"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        mb={2}
                        mt={groupIdx > 0 ? 1 : 0}
                      >
                        {group.categoryName}
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} w="full">
                        {group.machines.map((machine) => {
                          const outOfService = (machine.status || "").trim().toLowerCase() === "out of service";
                          const userHasPermission = isSelectable(machine);

                          return (
                            <ToolCard
                              isReadOnly={isSubmitting || completionUncertain}
                              isDisabled={!userHasPermission || outOfService}
                              unavailableLabel={outOfService ? "Out of service" : "Badge required"}
                              onUnavailable={() => {
                                setUnavailableInstance(outOfService ? machine.name : null);
                                setMachineTypeWarn(machineTypeIdToName(machine.machineType));
                                setIsPermissionWarningOpen(true);
                              }}
                              key={machine.name + machine.id}
                              type={machine.categoryName || "Null"}
                              name={machine.name}
                              isSelected={machine.isSelected}
                              onClick={() => {
                                if (isSubmitting || completionUncertain) return;
                                if (userHasPermission) {
                                  const updatedMachines = machines.map(m => {
                                    if (m.id === machine.id) {
                                      return {...m, isSelected: !m.isSelected};
                                    }
                                    return m;
                                  });
                                  setMachines(updatedMachines);
                                } else {
                                  setIsPermissionWarningOpen(true);
                                  let machineType = machineTypeIdToName(machine.machineType);
                                  setMachineTypeWarn(machineType);
                                }
                              }}
                            />
                          );
                        })}
                      </SimpleGrid>
                    </Box>
                  ))}
                </Flex>

              </Flex>
          ) : (
            <Box my={12}><KioskLoadingAnimation /></Box>
          )}

          <HStack justify="center" spacing={4} py={2} flexShrink={0}>
            {!hasSideArrowSpace && totalPages > 1 && <Button variant="outline" minH="56px" onClick={() => setCurrentPage(p => p - 1)} isDisabled={currentPage === 0} aria-label="Previous page"><ArrowLeft aria-hidden="true" /></Button>}
            <PageIndicator currentPage={currentPage} totalPages={totalPages} />
            {!hasSideArrowSpace && totalPages > 1 && <Button variant="outline" minH="56px" onClick={() => setCurrentPage(p => p + 1)} isDisabled={currentPage >= totalPages - 1} aria-label="Next page"><ArrowRight aria-hidden="true" /></Button>}
          </HStack>
        </Flex>

        {toastElements}
        <KioskActionBar>
          <Flex w="full" gap={4} direction={{ base: "column", md: "row" }}>
            <VisuallyHidden role="status">{selectedCount} selected</VisuallyHidden>
            <Button variant="outline" size="kiosk" flex={1} w="full" minW={0} whiteSpace="normal"
              isDisabled={selectedCount === 0 && !reviewSelected}
              onClick={() => { setReviewSelected(value => !value); setCurrentPage(0); }} aria-pressed={reviewSelected}>
              {reviewSelected ? "All tools" : selectedCount > 0 ? `Review selected (${selectedCount})` : canCompleteWithoutTools ? "No tools selected" : "Select a tool to continue"}
            </Button>
            <Button
              size="kiosk"
              flex={1}
              w="full"
              minW={0}
              whiteSpace="normal"
              isLoading={isSubmitting}
              loadingText={isUpdatingUsage ? "Saving tool usage…" : "Completing check-in…"}
              isDisabled={!catalogReady || (selectedCount === 0 && !canCompleteWithoutTools)}
              onClick={completeSelection}
            >
              {completionLabel}
            </Button>
          </Flex>
        </KioskActionBar>

        <Modal isOpen={isCategoryFilterOpen} onClose={() => setIsCategoryFilterOpen(false)} isCentered scrollBehavior="inside" size="xl">
          <ModalOverlay />
          <ModalContent mx={4}>
            <ModalHeader>Filter by category</ModalHeader>
            <ModalCloseButton aria-label="Close category filters" />
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {categories.slice().sort((a, b) => a.categoryName.localeCompare(b.categoryName)).map(category =>
                  <Button key={category.categoryID} minH="56px" h="auto" py={3} whiteSpace="normal"
                    variant="outline" fontSize="xl"
                    bg={selectedCategories.has(category.categoryID) ? "blue.700" : "white"}
                    color={selectedCategories.has(category.categoryID) ? "white" : "gray.800"}
                    _hover={{ bg: selectedCategories.has(category.categoryID) ? "blue.800" : "gray.50" }}
                    colorScheme="blue" aria-pressed={selectedCategories.has(category.categoryID)}
                    onClick={() => { setReviewSelected(false); toggleCategory(category.categoryID); }}>
                    {category.categoryName}
                  </Button>)}
              </SimpleGrid>
            </ModalBody>
            <ModalFooter gap={3} flexWrap="wrap">
              <Button variant="outline" minH="48px" onClick={() => { setSelectedCategories(new Set()); setReviewSelected(false); setIsCategoryFilterOpen(false); }}>All categories</Button>
              <Button minH="48px" onClick={() => setIsCategoryFilterOpen(false)}>Done</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {/* Permission warning modal */}
        {isPermissionWarningOpen && (
          <Modal isOpen={isPermissionWarningOpen} onClose={() => setIsPermissionWarningOpen(false)} isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent rounded="lg" mx={4} maxW="container.sm">
              <ModalHeader pr={14} color="gray.800" fontSize="2xl" fontWeight={700}>{unavailableInstance ? "Tool unavailable" : "Additional credential required"}</ModalHeader>
              <ModalCloseButton size="lg" w={12} h={12} p={0} fontSize="xl" aria-label="Close tool details" />
              <ModalBody>
                {unavailableInstance ? (
                  <Text fontSize="xl">{unavailableInstance} is out of service and cannot be selected. Please choose another tool or ask space staff for help.</Text>
                ) : (
                  <ToolCredentialDetails tool={machineTypeWarn} disconnected={badgesNotConnected} expired={badgeConnectionExpired} />
                )}
              </ModalBody>
              <ModalFooter><Button size="kiosk" w="full" onClick={() => setIsPermissionWarningOpen(false)}>Close</Button></ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Flex>
    );
  }
}
ToolSelection.propTypes = {
  user: PropTypes.shape({
    UID: PropTypes.number.isRequired,
  }).isRequired,
  lab: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default withRouter(ToolSelection);
